import { Head, router, useForm } from "@inertiajs/react"
import {
  ChevronRight,
  ClipboardList,
  FileText,
  ListChecks,
  MapPin,
  Pencil,
  Plus,
  RotateCcw,
  Send,
  Sparkles,
  Tags,
  Trash2,
  Truck,
  Users,
} from "lucide-react"
import { type ComponentType, useMemo, useState } from "react"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { useOrgRoutes } from "@/hooks/use-org-routes"
import AppLayout from "@/layouts/app-layout"
import { cn } from "@/lib/utils"
import type { BreadcrumbItem } from "@/types"

interface Category {
  key: string
  label: string
}

interface MasterValueRow {
  id: number
  category: string
  value: string
  position: number
  active: boolean
}

interface Props {
  categories: Category[]
  values: MasterValueRow[]
  can_manage: boolean
}

// One icon per category the code names. An operator-created category falls
// back to the generic tag — the list still renders, it just isn't decorated.
const CATEGORY_ICONS: Record<string, ComponentType<{ className?: string }>> = {
  fleet_code: Truck,
  defect_category: ClipboardList,
  otma_type: Send,
  qual_item: ListChecks,
  doc_type: FileText,
  cleaning_reason: Sparkles,
  repair_location: MapPin,
  counterparty_type: Users,
}

export default function MasterDataIndex({
  categories,
  values,
  can_manage,
}: Props) {
  const routes = useOrgRoutes()

  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Dashboard", href: routes.dashboardPath() },
    { title: "Master Data", href: routes.masterDataPath() },
  ]

  // The controller already emits values in (position, value) order, so this
  // only has to partition — never re-sort.
  const byCategory = useMemo(() => {
    const map = new Map<string, MasterValueRow[]>()
    for (const row of values) {
      const bucket = map.get(row.category)
      if (bucket) bucket.push(row)
      else map.set(row.category, [row])
    }
    return map
  }, [values])

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Master Data" />

      <div className="space-y-6 p-6">
        <div>
          <h1 className="text-2xl font-semibold">Master Data</h1>
          <p className="text-muted-foreground mt-1 text-sm">
            The dropdown lists the rest of the app reads from. Add your own
            values as you need them — extraction and import add to these lists
            too, so a value that shows up here came from somewhere real.
          </p>
        </div>

        <div className="space-y-3">
          {categories.map((category) => (
            <CategorySection
              key={category.key}
              category={category}
              rows={byCategory.get(category.key) ?? []}
              canManage={can_manage}
            />
          ))}
        </div>
      </div>
    </AppLayout>
  )
}

function CategorySection({
  category,
  rows,
  canManage,
}: {
  category: Category
  rows: MasterValueRow[]
  canManage: boolean
}) {
  const [open, setOpen] = useState(false)
  const [creating, setCreating] = useState(false)
  const [renaming, setRenaming] = useState<MasterValueRow | null>(null)
  const [retiring, setRetiring] = useState<MasterValueRow | null>(null)

  const Icon = CATEGORY_ICONS[category.key] ?? Tags
  const activeCount = rows.filter((r) => r.active).length
  const retiredCount = rows.length - activeCount

  return (
    <Card>
      <Collapsible open={open} onOpenChange={setOpen}>
        <CardHeader>
          <div className="flex items-start gap-3">
            <div className="bg-muted text-muted-foreground flex size-9 shrink-0 items-center justify-center rounded-md">
              <Icon className="size-5" />
            </div>
            <div className="min-w-0 flex-1">
              <CardTitle className="truncate">{category.label}</CardTitle>
              <p className="text-muted-foreground mt-1 text-sm tabular-nums">
                {activeCount === 0
                  ? "No values yet"
                  : `${activeCount} value${activeCount === 1 ? "" : "s"}`}
                {retiredCount > 0 && ` · ${retiredCount} retired`}
              </p>
            </div>
            <div className="flex shrink-0 items-center gap-1">
              {canManage && (
                <Button size="sm" onClick={() => setCreating(true)}>
                  <Plus className="mr-1 size-4" /> Add
                </Button>
              )}
              {rows.length > 0 && (
                <CollapsibleTrigger asChild>
                  <Button variant="ghost" size="sm">
                    <ChevronRight
                      className={cn(
                        "size-4 transition-transform",
                        open && "rotate-90",
                      )}
                    />
                    {open ? "Hide" : canManage ? "Manage" : "View"}
                  </Button>
                </CollapsibleTrigger>
              )}
            </div>
          </div>
        </CardHeader>

        {rows.length > 0 && (
          <CollapsibleContent>
            <CardContent>
              <ul className="divide-border divide-y">
                {rows.map((row) => (
                  <ValueRow
                    key={row.id}
                    row={row}
                    canManage={canManage}
                    onRename={() => setRenaming(row)}
                    onRetire={() => setRetiring(row)}
                  />
                ))}
              </ul>
            </CardContent>
          </CollapsibleContent>
        )}
      </Collapsible>

      {creating && (
        <CreateValueDialog
          category={category}
          existing={rows}
          onClose={() => setCreating(false)}
        />
      )}
      {renaming && (
        <RenameValueDialog
          row={renaming}
          existing={rows}
          onClose={() => setRenaming(null)}
        />
      )}
      {retiring && (
        <RetireValueDialog row={retiring} onClose={() => setRetiring(null)} />
      )}
    </Card>
  )
}

function ValueRow({
  row,
  canManage,
  onRename,
  onRetire,
}: {
  row: MasterValueRow
  canManage: boolean
  onRename: () => void
  onRetire: () => void
}) {
  const routes = useOrgRoutes()
  const [restoring, setRestoring] = useState(false)

  function restore() {
    setRestoring(true)
    router.patch(
      routes.masterDatumPath(row.id),
      { master_value: { active: true } },
      { preserveScroll: true, onFinish: () => setRestoring(false) },
    )
  }

  return (
    <li className="flex items-center justify-between gap-3 py-2">
      <div className="flex min-w-0 items-center gap-2">
        <span
          className={cn(
            "truncate font-medium",
            !row.active && "text-muted-foreground line-through",
          )}
        >
          {row.value}
        </span>
        {!row.active && <Badge variant="secondary">Retired</Badge>}
      </div>
      {canManage && (
        <div className="flex shrink-0 items-center gap-1">
          {row.active ? (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={onRename}
                aria-label={`Rename ${row.value}`}
              >
                <Pencil className="size-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={onRetire}
                aria-label={`Retire ${row.value}`}
              >
                <Trash2 className="size-4" />
              </Button>
            </>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              onClick={restore}
              disabled={restoring}
            >
              <RotateCcw className="mr-1 size-4" /> Restore
            </Button>
          )}
        </div>
      )}
    </li>
  )
}

// Case-insensitive collision check against the values already in this
// category — the server enforces the same uniqueness, this just says so before
// the round-trip. Ruby additionally canonicalizes ("BO-VRV" vs "bo vrv"), which
// the client deliberately does NOT replicate: one normalizer, server-side.
function collisionWith(
  candidate: string,
  existing: MasterValueRow[],
  ignoreId?: number,
): MasterValueRow | undefined {
  const needle = candidate.trim().toLowerCase()
  if (!needle) return undefined
  return existing.find(
    (row) => row.id !== ignoreId && row.value.toLowerCase() === needle,
  )
}

function CreateValueDialog({
  category,
  existing,
  onClose,
}: {
  category: Category
  existing: MasterValueRow[]
  onClose: () => void
}) {
  const routes = useOrgRoutes()
  const form = useForm({ category: category.key, value: "" })
  const collision = collisionWith(form.data.value, existing)

  function submit(e: React.FormEvent) {
    e.preventDefault()
    // The controller reads params[:master_value], so nest on the way out
    // rather than nesting `data` — a flat shape keeps `errors.value` bound to
    // the input above.
    form.transform((data) => ({ master_value: data }))
    form.post(routes.masterDataPath(), {
      preserveScroll: true,
      onSuccess: onClose,
    })
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <form onSubmit={submit}>
          <DialogHeader>
            <DialogTitle>Add to {category.label}</DialogTitle>
            <DialogDescription>
              This value becomes selectable everywhere {category.label} is
              chosen.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-1 py-4">
            <Label htmlFor={`add-${category.key}`}>Value</Label>
            <Input
              id={`add-${category.key}`}
              value={form.data.value}
              onChange={(e) => form.setData("value", e.target.value)}
              autoFocus
            />
            {collision && (
              <p className="text-destructive text-xs">
                Already in this list as &ldquo;{collision.value}&rdquo;
                {collision.active ? "" : " (retired — restore it instead)"}.
              </p>
            )}
            {form.errors.value && (
              <p className="text-destructive text-xs">{form.errors.value}</p>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={onClose}
              disabled={form.processing}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={
                form.processing || !form.data.value.trim() || !!collision
              }
            >
              Add
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function RenameValueDialog({
  row,
  existing,
  onClose,
}: {
  row: MasterValueRow
  existing: MasterValueRow[]
  onClose: () => void
}) {
  const routes = useOrgRoutes()
  const form = useForm({ value: row.value })
  const collision = collisionWith(form.data.value, existing, row.id)

  function submit(e: React.FormEvent) {
    e.preventDefault()
    form.transform((data) => ({ master_value: data }))
    form.patch(routes.masterDatumPath(row.id), {
      preserveScroll: true,
      onSuccess: onClose,
    })
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <form onSubmit={submit}>
          <DialogHeader>
            <DialogTitle>Rename &ldquo;{row.value}&rdquo;</DialogTitle>
            <DialogDescription>
              Records store the value itself, so renaming here re-labels every
              historical record that used it.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-1 py-4">
            <Label htmlFor={`rename-${row.id}`}>Value</Label>
            <Input
              id={`rename-${row.id}`}
              value={form.data.value}
              onChange={(e) => form.setData("value", e.target.value)}
              autoFocus
            />
            {collision && (
              <p className="text-destructive text-xs">
                Already in this list as &ldquo;{collision.value}&rdquo;.
              </p>
            )}
            {form.errors.value && (
              <p className="text-destructive text-xs">{form.errors.value}</p>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={onClose}
              disabled={form.processing}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={
                form.processing ||
                !form.data.value.trim() ||
                !!collision ||
                form.data.value === row.value
              }
            >
              Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function RetireValueDialog({
  row,
  onClose,
}: {
  row: MasterValueRow
  onClose: () => void
}) {
  const routes = useOrgRoutes()
  const [submitting, setSubmitting] = useState(false)

  function retire() {
    setSubmitting(true)
    router.delete(routes.masterDatumPath(row.id), {
      preserveScroll: true,
      onSuccess: onClose,
      onFinish: () => setSubmitting(false),
    })
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Retire &ldquo;{row.value}&rdquo;?</DialogTitle>
          <DialogDescription>
            It stops appearing in the dropdown. Records that already carry this
            value keep it — nothing is deleted, and you can restore it later.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={retire} disabled={submitting}>
            Retire
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
